/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Auth } from '@aws-amplify/auth';
import { Hub } from 'aws-amplify';

import MasterDataApiService from '../../../../Shared/Services/MasterDataService';
import { ILoginCustomer } from '../../../../Shared/Models';
import { AuthenticationService } from '../../../../Shared/Services/AuthenticationService';
import { Role, UserType } from '../../../../Shared/Constants/App';
import { IRole } from '../../../../App';

const POLL_INTERVAL_MS = 500;
const POLL_MAX_ATTEMPTS = 20; // 10 seconds total

/** Fetches customer list and caches it to localStorage */
/** Caches org + process data to localStorage */
const fetchAndCacheProcesses = async (customer: any): Promise<void> => {
  if (!customer?.data) return;
  localStorage.setItem('customerDetails', JSON.stringify(customer.data));
  const { organization, userId } = customer.data as { organization: { organizationId: string }; userId: string };
  const processes = await AuthenticationService.fetchAllProcesses(userId, organization.organizationId);
  if (processes?.data) {
    localStorage.setItem('processData', JSON.stringify(processes.data));
  }
};

/**
 * Infers userType from userScopes when custom:Role is not set in Cognito attributes.
 * This handles SSO users (e.g. Microsoft via Unifi-MS-Dev) whose Cognito profile
 * may not have the custom:Role attribute mapped.
 *
 * Heuristic: if any userScope has a customerId the user is a Customer; otherwise Vendor.
 */
const inferUserType = (userScopesResponse: any, customersList: ILoginCustomer[] | null): UserType | null => {
  const data: any[] = userScopesResponse?.data ?? [];
  if (!data.length) return null;
  // The slim login response has no per-customer scopes, so its customer list answers the same question.
  if (customersList) return customersList.length ? UserType.Customer : UserType.Vendor;

  const hasCustomerScopes = data.some((scope: any) =>
    (scope?.userScopes as any[])?.some((us: any) => !!us?.customerId)
  );
  return hasCustomerScopes ? UserType.Customer : UserType.Vendor;
};

/** Core session-setup logic, mirrors App.tsx signIn handler */
const buildSession = async (): Promise<void> => {
  // Registered before its first await, so API calls made meanwhile wait for the loginToken.
  const { customer, loggedInUser, userId } = await AuthenticationService.trackLoginSetup(
    (async () => {
      // One call for the user and their organization (userExist v2).
      const [{ customer, userId }, loggedInUser] = await Promise.all([
        AuthenticationService.getSignedInUserDetails(),
        MasterDataApiService.getCurrentUserInfo(),
      ]);

      // Also sent by the App.tsx Hub handler; the service dedupes, so only one session is opened.
      // Needed here because the Hub signIn event can fire before App mounts its listener.
      await AuthenticationService.sendSignInAuthEvent(userId, customer?.data?.organization?.organizationId);
      return { customer, loggedInUser, userId };
    })()
  );

  const { userScopesResponse, customersList } = await MasterDataApiService.getLoginRolesAndCustomers(
    userId,
    customer?.data?.organization?.organizationId
  );
  if (customersList) localStorage.setItem('customersList', JSON.stringify(customersList));

  let userRoles: string[] = [];
  if (userScopesResponse.data) {
    userRoles = (userScopesResponse.data as any[])
      .map((scope) => (scope?.roles as IRole[])?.map((role) => role.roleName))
      .flat();
    loggedInUser.canCreateOneTimeJob = (userScopesResponse.data as any[])[0]?.canCreateOneTimeJob ?? false;
  }

  // custom:Role may be absent for SSO users — infer it from scopes as fallback
  let userType: string = loggedInUser.attributes['custom:Role'];
  if (!userType) {
    const inferred = inferUserType(userScopesResponse, customersList);
    if (inferred) {
      userType = inferred;
      // Patch the attribute so it persists correctly in localStorage
      loggedInUser.attributes['custom:Role'] = userType;
    }
  }

  if (userType === UserType.Customer) userRoles = [Role.Customer];
  loggedInUser.roles = userRoles;

  if (userType !== UserType.Customer && userType !== UserType.Vendor) {
    localStorage.setItem('sso_login_error', 'sso_not_provisioned');
    await Auth.signOut();
    return;
  }

  localStorage.setItem('auth', JSON.stringify({ ...loggedInUser, signedInTime: new Date().getTime() }));
  await fetchAndCacheProcesses(customer);
  window.location.pathname = '/home';
};

const OAuthCallback = (): JSX.Element => {
  const [statusText, setStatusText] = useState('Signing you in...');
  const isHandled = useRef(false);

  useEffect(() => {
    let cancelled = false;

    // If Cognito redirected here with an error (e.g. invalid username/provider combination),
    // bail out immediately rather than polling for 10 seconds.
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const errorParam = urlParams.get('error') ?? hashParams.get('error');
    if (errorParam) {
      const errorDescription = urlParams.get('error_description') ?? hashParams.get('error_description') ?? errorParam;
      console.error('OAuthCallback: IdP/Cognito error:', errorDescription);
      window.location.href = '/login?error=sso_failed';
      return;
    }

    const setupSession = async (): Promise<void> => {
      if (isHandled.current) return;
      isHandled.current = true;
      try {
        setStatusText('Setting up your session...');
        await buildSession();
      } catch (error) {
        console.error('OAuthCallback: session setup failed:', error);
        localStorage.setItem('sso_login_error', 'sso_processing_failed');
        await Auth.signOut().catch(() => {});
      }
    };

    // Hub listener — catches events that fire after this component mounts
    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
        case 'implicitFlow':
          void setupSession();
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          window.location.href = '/login?error=sso_failed';
          break;
        default:
          break;
      }
    });

    // Primary path: with responseType 'token', Amplify stores tokens synchronously
    // during Amplify.configure() so currentAuthenticatedUser() resolves on the first attempt.
    // Polling handles any edge case where the token storage is slightly delayed.
    const pollForAuth = async (): Promise<void> => {
      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        if (cancelled || isHandled.current) return;
        try {
          await Auth.currentAuthenticatedUser();
          if (!cancelled) void setupSession();
          return;
        } catch {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
      }
      if (!cancelled && !isHandled.current) {
        localStorage.setItem('sso_login_error', 'sso_processing_failed');
        await Auth.signOut().catch(() => {});
      }
    };

    void pollForAuth();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" gap={2}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {statusText}
      </Typography>
    </Box>
  );
};

export default OAuthCallback;
